import React, { createContext, useContext, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';

const PaginationContext = createContext(null);

export const usePaginationContext = () => {
	const context = useContext(PaginationContext);
	if (!context) {
		throw new Error(
			'usePaginationContext must be used within PaginationProvider'
		);
	}
	return context;
};

export const PaginationProvider = ({
	children,
	totalCount,
	itemsPerPage,
	offset,
	onPageChange
}) => {
	// Offsets are page starts, so the ceiling has to be one too: clamping to
	// totalCount would land mid-page and slice to nothing (totalCount 11,
	// itemsPerPage 6 would stop at 11, and [11, 11) is empty).
	const lastPageOffset =
		Math.max(Math.ceil(totalCount / itemsPerPage) - 1, 0) * itemsPerPage;

	const goToNextPage = useCallback(() => {
		onPageChange((prev) => Math.min(prev + itemsPerPage, lastPageOffset));
	}, [itemsPerPage, lastPageOffset, onPageChange]);

	const goToPreviousPage = useCallback(() => {
		onPageChange((prev) => Math.max(0, prev - itemsPerPage));
	}, [itemsPerPage, onPageChange]);

	const canGoNext = offset + itemsPerPage < totalCount;
	const canGoPrevious = offset - itemsPerPage >= 0;

	const value = useMemo(
		() => ({
			offset,
			totalCount,
			itemsPerPage,
			canGoNext,
			canGoPrevious,
			goToNextPage,
			goToPreviousPage
		}),
		[
			offset,
			totalCount,
			itemsPerPage,
			canGoNext,
			canGoPrevious,
			goToNextPage,
			goToPreviousPage
		]
	);
	return (
		<PaginationContext.Provider value={value}>
			{children}
		</PaginationContext.Provider>
	);
};

PaginationProvider.propTypes = {
	children: PropTypes.node.isRequired,
	totalCount: PropTypes.number.isRequired,
	onPageChange: PropTypes.func.isRequired,
	itemsPerPage: PropTypes.number.isRequired,
	offset: PropTypes.number.isRequired
};
